import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Sparkles,
    Check,
    Zap,
    Loader2,
    Image as ImageIcon,
    ShieldCheck,
    CheckCircle2,
    AlertTriangle,
    Coins,
    Ticket,
} from "lucide-react";
import TopNav from "../components/TopNav";
import {
    listSubscriptionTiers,
    getMySubscription,
    fetchSubscriptionTierImage,
    getRevenueCatPublicConfig,
    refreshMySubscription,
    listCreditPacks,
    claimCreditPack,
    redeemPromoCode,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { getPurchasesInstance, findPackageForProduct } from "../lib/revenuecat";
import { adminGetFeatureFlags, adminUpdateFeatureFlags } from "../lib/api";

function fmtDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return "";
    }
}

// iter-141 — superadmin-only kill switch mounted at the top of the
// Subscriptions page. Reads/writes the `show_subscriptions` feature
// flag (same one previously buried in Admin → Future flags). When
// OFF, `CreditBalancePill` and the paywall/redirects all return null
// so end users never see the red "Subscribe" bullet in the top nav.
function SubscriptionsKillSwitch() {
    const { user } = useAuth();
    const [enabled, setEnabled] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const isSuperadmin = (user?.role || "") === "superadmin";

    useEffect(() => {
        if (!isSuperadmin) { setLoading(false); return; }
        let dead = false;
        (async () => {
            try {
                const f = await adminGetFeatureFlags();
                if (!dead) setEnabled(f?.show_subscriptions !== false);
            } catch (err) {
                if (!dead) toast.error(err?.response?.data?.detail || "Couldn't load flag.");
            } finally {
                if (!dead) setLoading(false);
            }
        })();
        return () => { dead = true; };
    }, [isSuperadmin]);

    if (!isSuperadmin || loading) return null;

    const handleToggle = async () => {
        const next = !enabled;
        setSaving(true);
        try {
            await adminUpdateFeatureFlags({ show_subscriptions: next });
            setEnabled(next);
            toast.success(
                next
                    ? "Subscriptions enabled — credit pill & paywall are back."
                    : "Subscriptions disabled — credit pill & paywall hidden from users.",
            );
            // Trigger a page reload so the feature-flag cache in the
            // current tab's TopNav updates too (the flag hook fetches
            // on mount).
            setTimeout(() => window.location.reload(), 400);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update flag.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="subs-kill-switch"
            className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#2A3655] bg-[#131A2F]/80 px-4 py-3"
        >
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-rose-300 mb-0.5">
                    Superadmin control
                </div>
                <div className="text-sm text-white font-bold">
                    {enabled ? "Subscriptions are ENABLED" : "Subscriptions are DISABLED"}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                    {enabled
                        ? "Users see the credit pill in the top nav, the Subscription menu entry, and paywall redirects."
                        : "Every user's credit pill is hidden. Menu entry and paywall redirects are suppressed too."}
                </div>
            </div>
            <button
                type="button"
                onClick={handleToggle}
                disabled={saving}
                data-testid="subs-kill-switch-toggle"
                aria-pressed={enabled}
                className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs uppercase tracking-wider font-bold border transition-colors disabled:opacity-50 ${
                    enabled
                        ? "bg-rose-500/10 border-rose-500/40 text-rose-200 hover:bg-rose-500/20"
                        : "bg-emerald-500/10 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/20"
                }`}
            >
                {saving ? "Saving…" : enabled ? "Disable subscriptions" : "Enable subscriptions"}
            </button>
        </div>
    );
}

// Per-tier image with lazy load via object-storage URL.
function TierImage({ storagePath, alt }) {
    const [src, setSrc] = useState("");
    useEffect(() => {
        let dead = false;
        if (!storagePath) return;
        fetchSubscriptionTierImage(storagePath)
            .then((d) => !dead && setSrc(d.data_url || ""))
            .catch(() => {});
        return () => {
            dead = true;
        };
    }, [storagePath]);
    if (!src) {
        return (
            <div className="aspect-[16/9] w-full bg-[#0A1128] border-b border-[#2A3655] flex items-center justify-center text-slate-600">
                <ImageIcon className="w-8 h-8" />
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={alt || ""}
            className="aspect-[16/9] w-full object-cover border-b border-[#2A3655]"
        />
    );
}

function TierCard({ tier, currentTierId, onSubscribe, subscribing }) {
    const isCurrent = currentTierId === tier.id;
    const lines = (tier.description || "")
        .split(/\n+/)
        .map((s) => s.trim())
        .filter(Boolean);

    return (
        <article
            data-testid={`tier-card-${tier.id}`}
            className={`relative bg-[#131A2F]/80 border rounded-md overflow-hidden flex flex-col transition-all ${
                isCurrent
                    ? "border-[#00A3FF] shadow-[0_0_24px_rgba(0,163,255,0.18)]"
                    : "border-[#2A3655] hover:border-[#00A3FF]/60"
            }`}
        >
            {isCurrent && (
                <span className="absolute top-3 right-3 z-10 text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm bg-[#00A3FF]/20 border border-[#00A3FF]/50 text-[#00A3FF] font-bold">
                    Current plan
                </span>
            )}
            <TierImage storagePath={tier.image_storage_path} alt={tier.title} />
            <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-white tracking-tight">
                    {tier.title}
                </h3>
                <div className="mt-1 flex items-baseline gap-1.5">
                    <span className="text-3xl font-bold text-white tabular-nums">
                        ${Number(tier.price_usd).toFixed(2)}
                    </span>
                    <span className="text-xs text-slate-400">
                        / {tier.duration_days} days
                    </span>
                </div>
                <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-[#007AFF]/10 border border-[#007AFF]/40 text-[#00A3FF] text-xs font-semibold w-fit">
                    <Sparkles className="w-3 h-3" />
                    {Number(tier.credit_amount).toLocaleString()} credits per period
                </div>
                {lines.length > 0 && (
                    <ul className="mt-4 space-y-1.5 text-sm text-slate-300 flex-1">
                        {lines.map((line, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-2"
                                data-testid={`tier-feature-${tier.id}-${i}`}
                            >
                                <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>
                )}
                <button
                    type="button"
                    onClick={() => onSubscribe(tier)}
                    disabled={subscribing || isCurrent}
                    data-testid={`tier-subscribe-${tier.id}`}
                    className={`mt-6 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md font-bold uppercase tracking-wider text-sm transition-colors ${
                        isCurrent
                            ? "bg-[#1C253C] border border-[#2A3655] text-slate-400 cursor-default"
                            : "bg-[#007AFF] hover:bg-[#3395FF] text-white"
                    } disabled:opacity-60`}
                >
                    {subscribing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isCurrent ? (
                        <CheckCircle2 className="w-4 h-4" />
                    ) : (
                        <Zap className="w-4 h-4" />
                    )}
                    {isCurrent ? "Active" : "Subscribe"}
                </button>
            </div>
        </article>
    );
}

export default function SubscriptionsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [tiers, setTiers] = useState([]);
    const [mine, setMine] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rcConfig, setRcConfig] = useState(null);
    const [subscribing, setSubscribing] = useState(false); // false | tier.id

    useEffect(() => {
        let dead = false;
        (async () => {
            setLoading(true);
            try {
                const [list, sub, cfg] = await Promise.all([
                    listSubscriptionTiers(),
                    getMySubscription().catch(() => ({})),
                    getRevenueCatPublicConfig().catch(() => null),
                ]);
                if (dead) return;
                setTiers(list);
                setMine(sub);
                setRcConfig(cfg);
                // If we returned from a hosted purchase link, the URL
                // will carry ?subscribe=success — force a reconcile so
                // the local subscription state catches up before the
                // webhook lands.
                if (typeof window !== "undefined") {
                    const params = new URLSearchParams(window.location.search);
                    if (params.get("subscribe") === "success") {
                        try {
                            await refreshMySubscription();
                            const fresh = await getMySubscription();
                            if (!dead) setMine(fresh);
                            toast.success("Subscription activated!");
                        } catch (e) {
                            // Webhook will eventually reconcile — log so
                            // we can spot RC misconfig in DevTools.
                            // eslint-disable-next-line no-console
                            console.warn("Post-subscribe reconcile failed:", e);
                        }
                        // Clean the query param so a refresh doesn't
                        // re-trigger the reconcile.
                        const url = new URL(window.location.href);
                        url.searchParams.delete("subscribe");
                        window.history.replaceState({}, "", url.toString());
                    }
                }
            } catch (err) {
                toast.error(
                    err?.response?.data?.detail || "Couldn't load plans.",
                );
            } finally {
                if (!dead) setLoading(false);
            }
        })();
        return () => {
            dead = true;
        };
    }, []);

    const balance = mine?.balance || {};
    const currentTierId = balance.has_subscription ? balance.tier_id : "";

    const handleSubscribe = async (tier) => {
        if (!rcConfig) {
            toast.error("Loading billing config — try again in a moment.");
            return;
        }
        const hasWebKey = !!rcConfig.web_public_key;
        const hasLink = !!rcConfig.web_purchase_link_url;
        if (!hasWebKey && !hasLink) {
            toast.error(
                "Billing isn't configured yet — ask the admin to add the RevenueCat keys.",
            );
            return;
        }
        setSubscribing(tier.id);

        // Preferred: embedded purchases-js flow (modal checkout inside
        // the app, no redirect). Falls back to the hosted Web Purchase
        // Link if the SDK can't find the matching offering / package.
        if (hasWebKey) {
            try {
                const purchases = await getPurchasesInstance({
                    apiKey: rcConfig.web_public_key,
                    appUserId: user?.id || rcConfig.app_user_id,
                });
                const pkg = await findPackageForProduct(
                    purchases,
                    tier.revenuecat_product_id,
                );
                if (pkg) {
                    const result = await purchases.purchase({ rcPackage: pkg });
                    // Reconcile against RC immediately so the credit pill
                    // reflects the new entitlement without waiting for the
                    // webhook (which can take a few seconds).
                    try {
                        await refreshMySubscription();
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.warn("Post-purchase RC reconcile failed:", e);
                    }
                    toast.success("Subscription active — enjoy your credits!");
                    // Re-load page state.
                    const sub = await getMySubscription().catch(() => null);
                    if (sub) setMine(sub);
                    setSubscribing(false);
                    // eslint-disable-next-line no-unused-expressions
                    result; // (kept reference — RC returns customerInfo on success)
                    return;
                }
                // No matching offering — fall through to hosted link.
                // eslint-disable-next-line no-console
                console.warn(
                    "RevenueCat: no package found for product",
                    tier.revenuecat_product_id,
                    "— falling back to hosted purchase link.",
                );
            } catch (err) {
                if (err?.userCancelled || err?.code === "USER_CANCELLED") {
                    setSubscribing(false);
                    return;
                }
                // eslint-disable-next-line no-console
                console.warn("RevenueCat embedded purchase failed:", err);
                if (!hasLink) {
                    toast.error(
                        err?.message || "Couldn't open the checkout. Try again.",
                    );
                    setSubscribing(false);
                    return;
                }
                // else fall through to hosted link
            }
        }

        // Fallback: hosted Web Purchase Link redirect.
        const linkUrl = rcConfig.web_purchase_link_url || "";
        if (!linkUrl) {
            toast.error(
                "Embedded checkout unavailable and no Web Purchase Link configured.",
            );
            setSubscribing(false);
            return;
        }
        const sep = linkUrl.includes("?") ? "&" : "?";
        const target = `${linkUrl}${sep}app_user_id=${encodeURIComponent(
            user?.id || rcConfig.app_user_id || "",
        )}${
            tier.revenuecat_product_id
                ? `&offering=${encodeURIComponent(tier.revenuecat_product_id)}`
                : ""
        }`;
        window.location.href = target;
    };

    return (
        <div className="min-h-[100dvh] blueprint-bg text-slate-100 flex flex-col">
            <TopNav active="subscriptions" />
            <main
                className="flex-1 max-w-6xl w-full mx-auto px-4 md:px-8 py-8 md:py-12"
                data-testid="subscriptions-page"
            >
                <header className="mb-6 md:mb-10">
                    <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">
                        Plans & credits
                    </div>
                    <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-white mb-3">
                        Pick a tier that fits your jobs
                    </h1>
                    <p className="text-slate-400 text-sm md:text-base max-w-2xl">
                        Every plan refreshes its AI credits at the start of
                        each period. Unused credits don't carry over —
                        complex jobs (Takeoff, Analyze, Estimate) draw from
                        the same shared pool based on calls actually made.
                    </p>
                    {/* iter-141: superadmin can disable subscriptions
                        app-wide right from this page. When OFF the credit
                        pill in every user's top-nav, the Subscription
                        menu entry, and the paywall redirects all go
                        away. */}
                    <SubscriptionsKillSwitch />
                    {balance.has_subscription && (
                        <div
                            className="mt-5 inline-flex flex-wrap items-center gap-3 px-4 py-2.5 bg-[#131A2F]/80 border border-[#2A3655] rounded-sm"
                            data-testid="current-plan-summary"
                        >
                            <ShieldCheck className="w-4 h-4 text-emerald-400" />
                            <div className="text-sm">
                                <span className="text-white font-bold">
                                    {balance.tier_title}
                                </span>
                                <span className="text-slate-500 mx-2">·</span>
                                <span className="tabular-nums text-[#00A3FF]">
                                    {Number(balance.credits_remaining).toLocaleString()} /{" "}
                                    {Number(balance.credit_amount).toLocaleString()} credits
                                </span>
                                {balance.period_end && (
                                    <>
                                        <span className="text-slate-500 mx-2">·</span>
                                        <span className="text-slate-400">
                                            renews {fmtDate(balance.period_end)}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    {rcConfig && !rcConfig.configured && (
                        <div className="mt-5 inline-flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/40 text-amber-300 rounded-sm text-xs">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Billing not configured yet — admin must paste the
                            RevenueCat keys in <span className="font-bold">Admin → Integrations</span>.
                        </div>
                    )}
                </header>

                {loading ? (
                    <div className="text-slate-400" data-testid="subs-loading">
                        Loading plans…
                    </div>
                ) : tiers.length === 0 ? (
                    <div
                        className="px-6 py-12 text-center text-slate-400 bg-[#131A2F]/60 border border-dashed border-[#2A3655] rounded-md"
                        data-testid="subs-empty"
                    >
                        <Sparkles className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                        <p className="font-semibold">No plans available yet.</p>
                        <p className="text-xs mt-1">
                            The admin hasn't published subscription tiers.
                        </p>
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                        data-testid="subs-grid"
                    >
                        {tiers.map((t) => (
                            <TierCard
                                key={t.id}
                                tier={t}
                                currentTierId={currentTierId}
                                onSubscribe={handleSubscribe}
                                subscribing={subscribing === t.id}
                            />
                        ))}
                    </div>
                )}

                <CreditPacksSection
                    onClaimed={async () => {
                        const sub = await getMySubscription();
                        setMine(sub);
                    }}
                />

                <PromoCodeBox
                    onRedeemed={async () => {
                        const sub = await getMySubscription();
                        setMine(sub);
                    }}
                />

                <footer className="mt-10 pt-6 border-t border-[#2A3655] text-xs text-slate-500 space-y-1">
                    <p>
                        Web payments are processed by{" "}
                        <span className="text-slate-300 font-semibold">RevenueCat Web Billing</span>{" "}
                        (Stripe under the hood). Mobile users can subscribe
                        via the App Store or Google Play once the apps ship —
                        receipts route through the same RevenueCat account
                        so your credits move with you.
                    </p>
                </footer>
            </main>
        </div>
    );
}


// iter-95 ----------- credit-pack top-up section -----------

function CreditPacksSection({ onClaimed }) {
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");

    useEffect(() => {
        (async () => {
            try {
                setPacks(await listCreditPacks());
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn("Credit pack list failed:", e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return null;
    if (!packs.length) return null;

    const buy = async (pack) => {
        if (!pack.revenuecat_product_id) {
            toast.error(
                "This pack isn't connected to a payment product yet. Ask the owner to set the RevenueCat product ID in Admin.",
            );
            return;
        }
        setBusy(pack.id);
        try {
            const purchases = await getPurchasesInstance();
            if (!purchases) {
                throw new Error("RevenueCat isn't configured yet.");
            }
            const offerings = await purchases.getOfferings();
            const pkg = findPackageForProduct(
                offerings,
                pack.revenuecat_product_id,
            );
            if (!pkg) {
                throw new Error(
                    `No RevenueCat package matches product "${pack.revenuecat_product_id}".`,
                );
            }
            await purchases.purchase({ rcPackage: pkg });
            // RC purchase resolved — tell backend to grant.
            await claimCreditPack(pack.id);
            toast.success(`+${pack.credits} credits added to your balance!`);
            onClaimed?.();
        } catch (e) {
            const msg =
                e?.response?.data?.detail ||
                e?.message ||
                "Purchase failed.";
            toast.error(msg);
        } finally {
            setBusy("");
        }
    };

    return (
        <section
            className="mt-12"
            data-testid="credit-packs-section"
        >
            <div className="flex items-center gap-2 mb-3">
                <Coins className="w-5 h-5 text-amber-300" />
                <h2 className="text-xl sm:text-2xl font-black tracking-tight text-white">
                    Top up credits
                </h2>
            </div>
            <p className="text-sm text-slate-400 mb-5 max-w-prose">
                One-time credit packs. They never expire and stack on top of
                whatever's left in your monthly subscription.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {packs.map((p) => (
                    <div
                        key={p.id}
                        className="rounded-md border border-[#2A3655] bg-[#0F1B3A] p-5 flex flex-col"
                        data-testid={`credit-pack-${p.id}`}
                    >
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <h3 className="text-base font-bold text-white">
                                {p.title}
                            </h3>
                            <span className="text-[11px] uppercase tracking-wider font-bold text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-full px-2 py-0.5">
                                {p.credits} credits
                            </span>
                        </div>
                        {p.description && (
                            <p className="text-[13px] text-slate-400 mb-3">
                                {p.description}
                            </p>
                        )}
                        <div className="mt-auto pt-3 flex items-center justify-between gap-3">
                            <div className="text-2xl font-black text-white">
                                {p.price_usd > 0 ? (
                                    <>
                                        ${p.price_usd.toFixed(2)}
                                        <span className="text-xs text-slate-500 font-medium">{" "}USD</span>
                                    </>
                                ) : (
                                    <span className="text-slate-500 text-sm">Price TBD</span>
                                )}
                            </div>
                            <button
                                type="button"
                                disabled={busy === p.id}
                                onClick={() => buy(p)}
                                data-testid={`credit-pack-buy-${p.id}`}
                                className="px-4 py-2 text-sm font-bold rounded-sm bg-amber-500 hover:bg-amber-400 text-[#0A1128] transition-colors disabled:opacity-50"
                            >
                                {busy === p.id ? "…" : "Buy"}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}


// iter-95 ----------- promo code redemption -----------

function PromoCodeBox({ onRedeemed }) {
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e?.preventDefault?.();
        const c = (code || "").trim();
        if (!c) return;
        setBusy(true);
        try {
            const res = await redeemPromoCode(c);
            const eff = res?.effect || {};
            if (eff.credits) {
                toast.success(`Promo redeemed — +${eff.credits} credits added!`);
            } else if (eff.days_added) {
                toast.success(
                    `Promo redeemed — ${eff.days_added} days of ${eff.tier_title || "subscription"} added!`,
                );
            } else {
                toast.success("Promo redeemed!");
            }
            setCode("");
            onRedeemed?.();
        } catch (e2) {
            const msg =
                e2?.response?.data?.detail ||
                e2?.message ||
                "Couldn't redeem that code.";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            className="mt-12 rounded-md border border-[#2A3655] bg-[#0F1B3A] p-5"
            data-testid="promo-code-section"
        >
            <div className="flex items-center gap-2 mb-2">
                <Ticket className="w-5 h-5 text-emerald-300" />
                <h2 className="text-lg sm:text-xl font-black tracking-tight text-white">
                    Got a promo code?
                </h2>
            </div>
            <p className="text-sm text-slate-400 mb-4 max-w-prose">
                Enter the code we sent you to claim free credits or a free
                stretch of subscription time.
            </p>
            <form
                onSubmit={submit}
                className="flex flex-col sm:flex-row gap-2 max-w-md"
            >
                <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="ENTER CODE"
                    maxLength={40}
                    data-testid="promo-code-input"
                    className="flex-1 px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white uppercase tracking-wider font-mono outline-none focus:border-emerald-400"
                />
                <button
                    type="submit"
                    disabled={busy || !code.trim()}
                    data-testid="promo-code-redeem"
                    className="px-4 py-2 text-sm font-bold rounded-sm bg-emerald-500 hover:bg-emerald-400 text-[#0A1128] transition-colors disabled:opacity-40"
                >
                    {busy ? "…" : "Redeem"}
                </button>
            </form>
        </section>
    );
}
